import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Globe, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill } from '../componentes/ui/Pill.js';
import { SecaoRollover } from '../componentes/SecaoRollover.js';
import { ListaDs } from '../componentes/ListaDs.js';

const TIPOS = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV'] as const;

interface Dominio {
  id: string;
  nome: string;
  operadora: { nome: string };
}

interface Registro {
  nome: string;
  tipo: string;
  ttl: number;
  valores: string[];
  sistema: boolean;
}

export function Dominios() {
  const qc = useQueryClient();
  const [adicionar, setAdicionar] = useState(false);
  const [aberto, setAberto] = useState<Dominio | null>(null);

  const { data: dominios = [], isLoading } = useQuery({
    queryKey: ['dominios'],
    queryFn: () => api.get<Dominio[]>('/dominios'),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12.5px] text-texto-suave">
          Zonas autoritativas dos domínios dos clientes. Separado do reverso: aqui é o DNS
          "para frente" (site, e-mail, subdomínios).
        </p>
        <Botao variante="primario" onClick={() => setAdicionar(true)} iconeEsquerda={<Plus size={15} />}>
          Adicionar domínio
        </Botao>
      </div>

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-card bg-borda-tenue" />
      ) : dominios.length === 0 ? (
        <div className="rounded-card border border-borda bg-superficie p-8 text-center">
          <Globe size={28} className="mx-auto text-texto-mudo" />
          <p className="mt-3 text-[13.5px] font-semibold">Nenhum domínio ainda</p>
          <p className="mt-1 text-[12.5px] text-texto-suave">
            Cadastre o domínio antes de o cliente trocar os nameservers no registro.br.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <table className="w-full border-collapse text-[13.5px]">
            <tbody>
              {dominios.map((d) => (
                <tr key={d.id} className="border-b border-borda-suave last:border-0 hover:bg-borda-tenue">
                  <td className="px-4 py-3">
                    <span className="grid size-9 place-items-center rounded-chip bg-info-fundo text-info">
                      <Globe size={17} />
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <p className="font-mono font-semibold">{d.nome}</p>
                    <p className="text-[12px] text-texto-suave">{d.operadora.nome}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Botao tamanho="tabela" onClick={() => setAberto(d)}>
                      Gerenciar registros
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adicionar && (
        <ModalAdicionarDominio
          aoFechar={() => setAdicionar(false)}
          aoCriado={() => void qc.invalidateQueries({ queryKey: ['dominios'] })}
        />
      )}

      {aberto && <ModalRegistros dominio={aberto} aoFechar={() => setAberto(null)} />}
    </div>
  );
}

function ModalAdicionarDominio({
  aoFechar,
  aoCriado,
}: {
  aoFechar: () => void;
  aoCriado: () => void;
}) {
  const [nome, setNome] = useState('');
  const [operadoraId, setOperadoraId] = useState('');

  const { data: operadoras = [] } = useQuery({
    queryKey: ['operadoras'],
    queryFn: () => api.get<{ id: string; nome: string }[]>('/operadoras'),
  });

  const criar = useMutation({
    mutationFn: () => api.post<Dominio>('/dominios', { nome, operadoraId: operadoraId || undefined }),
    onSuccess: aoCriado,
  });

  const criado = criar.isSuccess;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={criado ? `${criar.data?.nome} cadastrado` : 'Adicionar domínio'}
      descricao={criado ? undefined : 'A zona é criada no nosso DNS com SOA e NS já apontando para os nós.'}
      rodape={
        criado ? (
          <Botao variante="primario" onClick={aoFechar}>
            Fechar
          </Botao>
        ) : (
          <>
            <Botao onClick={aoFechar} disabled={criar.isPending}>
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              onClick={() => criar.mutate()}
              carregando={criar.isPending}
              disabled={nome.trim().length < 3 || !operadoraId}
            >
              Criar zona
            </Botao>
          </>
        )
      }
    >
      {criado ? (
        <div className="space-y-3 text-[13px]">
          <p className="rounded-chip bg-sucesso-fundo px-3 py-2.5 font-semibold text-sucesso">
            Zona criada. Agora o cliente troca os nameservers no registro.br para:
          </p>
          <div className="rounded-chip bg-escuro px-4 py-3 font-mono text-[12.5px] text-white">
            rdns1.consultnetworks.com.br
            <br />
            rdns2.consultnetworks.com.br
          </div>
          <p className="leading-relaxed text-texto-suave">
            O registro.br valida a delegação antes de aceitar — como a zona já existe aqui, ele
            aceita. Só depois disso os registros que você criar passam a resolver no mundo.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
              Operadora
            </span>
            <select
              value={operadoraId}
              onChange={(e) => setOperadoraId(e.target.value)}
              className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
            >
              <option value="">Selecione a operadora…</option>
              {operadoras.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
              Domínio
            </span>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="cliente.com.br"
              spellCheck={false}
              className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
            />
          </label>

          {criar.error && (
            <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
              {(criar.error as ApiError).descricao}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function ModalRegistros({ dominio, aoFechar }: { dominio: Dominio; aoFechar: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<{ nome: string; tipo: string; ttl: number; valores: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['dominios', dominio.id, 'registros'],
    queryFn: () => api.get<{ registros: Registro[] }>(`/dominios/${dominio.id}/registros`),
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: ['dominios', dominio.id, 'registros'] });

  const salvar = useMutation({
    mutationFn: () =>
      api.put(`/dominios/${dominio.id}/registros`, {
        nome: form!.nome,
        tipo: form!.tipo,
        ttl: form!.ttl,
        valores: form!.valores.split('\n').map((v) => v.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      void invalidar();
      setForm(null);
    },
  });

  const remover = useMutation({
    mutationFn: (r: Registro) =>
      api.delete(`/dominios/${dominio.id}/registros?nome=${encodeURIComponent(r.nome)}&tipo=${r.tipo}`),
    onSuccess: () => void invalidar(),
  });

  const registros = data?.registros ?? [];

  return (
    <Modal aberto aoFechar={aoFechar} largura="largo" titulo={dominio.nome} descricao="Registros da zona">
      <div className="space-y-4">
        <SecaoDnssec dominioId={dominio.id} />

        {!form && (
          <div className="flex justify-end">
            <Botao
              tamanho="tabela"
              onClick={() => setForm({ nome: '', tipo: 'A', ttl: 3600, valores: '' })}
              iconeEsquerda={<Plus size={13} />}
            >
              Novo registro
            </Botao>
          </div>
        )}

        {form && (
          <div className="space-y-3 rounded-card border border-marca/30 bg-tint/40 p-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_120px_100px]">
              <label className="block">
                <Rotulo>Nome</Rotulo>
                <input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="@ (raiz) ou www"
                  className="h-10 w-full rounded-controle border border-borda px-3 font-mono text-[13px] focus:border-marca focus:outline-none"
                />
              </label>
              <label className="block">
                <Rotulo>Tipo</Rotulo>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  className="h-10 w-full rounded-controle border border-borda px-2 text-[13px] focus:border-marca focus:outline-none"
                >
                  {TIPOS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <Rotulo>TTL</Rotulo>
                <input
                  type="number"
                  value={form.ttl}
                  onChange={(e) => setForm({ ...form, ttl: Number(e.target.value) || 3600 })}
                  className="h-10 w-full rounded-controle border border-borda px-2 font-mono text-[13px] focus:border-marca focus:outline-none"
                />
              </label>
            </div>
            <label className="block">
              <Rotulo>Valor(es) — um por linha</Rotulo>
              <textarea
                value={form.valores}
                onChange={(e) => setForm({ ...form, valores: e.target.value })}
                rows={3}
                spellCheck={false}
                placeholder={dicaTipo(form.tipo)}
                className="w-full rounded-controle border border-borda px-3 py-2 font-mono text-[12.5px] focus:border-marca focus:outline-none"
              />
            </label>
            {salvar.error && (
              <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12px] font-semibold text-erro">
                {(salvar.error as ApiError).descricao}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Botao tamanho="tabela" onClick={() => setForm(null)}>
                Cancelar
              </Botao>
              <Botao
                tamanho="tabela"
                variante="primario"
                onClick={() => salvar.mutate()}
                carregando={salvar.isPending}
                disabled={!form.valores.trim()}
              >
                Salvar
              </Botao>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="h-32 animate-pulse rounded-chip bg-borda-tenue" />
        ) : (
          <div className="overflow-hidden rounded-chip border border-borda">
            <table className="w-full border-collapse text-[12.5px]">
              <tbody>
                {registros.map((r) => (
                  <tr key={`${r.nome}-${r.tipo}`} className="border-b border-borda-suave last:border-0">
                    <td className="px-3 py-2 align-top">
                      <Pill tom={r.sistema ? 'neutro' : 'info'}>{r.tipo}</Pill>
                    </td>
                    <td className="px-2 py-2 align-top font-mono text-texto-suave">{r.nome}</td>
                    <td className="px-2 py-2 align-top font-mono">
                      {r.valores.map((v, i) => (
                        <div key={i} className="break-all">
                          {v}
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      {!r.sistema && (
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() =>
                              setForm({ nome: r.nome, tipo: r.tipo, ttl: r.ttl, valores: r.valores.join('\n') })
                            }
                            className="text-[12px] font-semibold text-marca hover:underline"
                          >
                            editar
                          </button>
                          <button
                            onClick={() => remover.mutate(r)}
                            className="text-texto-suave hover:text-erro"
                            title="Remover"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-texto-suave">
          <AlertTriangle size={13} className="mt-px shrink-0" />
          Os registros valem no mundo só depois que o cliente apontar os nameservers no registro.br.
          SOA e NS da raiz sustentam a zona e não podem ser removidos.
        </p>
      </div>
    </Modal>
  );
}

interface EstadoDnssec {
  assinada: boolean;
  ds: string[];
  dsPublicado: boolean;
  rollover: {
    status: string;
    keyTagVelho: number | null;
    keyTagNovo: number | null;
    dsNovo: string[];
  } | null;
}

function SecaoDnssec({ dominioId }: { dominioId: string }) {
  const qc = useQueryClient();
  const chave = ['dominios', dominioId, 'dnssec'];

  const { data, isLoading } = useQuery({
    queryKey: chave,
    queryFn: () => api.get<EstadoDnssec>(`/dominios/${dominioId}/dnssec`),
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: chave });

  const ativar = useMutation({
    mutationFn: () => api.post(`/dominios/${dominioId}/dnssec`),
    onSuccess: () => void invalidar(),
  });
  const desativar = useMutation({
    mutationFn: () => api.delete(`/dominios/${dominioId}/dnssec`),
    onSuccess: () => void invalidar(),
  });
  const publicado = useMutation({
    mutationFn: (v: boolean) => api.put(`/dominios/${dominioId}/dnssec/publicado`, { publicado: v }),
    onSuccess: () => void invalidar(),
  });

  const iniciarRollover = useMutation({
    mutationFn: () => api.post(`/dominios/${dominioId}/dnssec/rollover`),
    onSuccess: () => void invalidar(),
  });
  const verificarRollover = useMutation({
    mutationFn: () => api.post<{ detectadoNovo?: boolean }>(`/dominios/${dominioId}/dnssec/rollover/verificar`),
    onSuccess: () => void invalidar(),
  });
  const cancelarRollover = useMutation({
    mutationFn: () => api.delete(`/dominios/${dominioId}/dnssec/rollover`),
    onSuccess: () => void invalidar(),
  });

  if (isLoading || !data) return null;

  return (
    <div className="rounded-card border border-borda bg-fundo p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className={data.assinada ? 'text-sucesso' : 'text-texto-mudo'} />
          <span className="text-[13px] font-bold">DNSSEC</span>
          <Pill tom={data.assinada ? 'ok' : 'neutro'}>{data.assinada ? 'assinada' : 'desligado'}</Pill>
        </div>
        {data.assinada ? (
          <button
            onClick={() => desativar.mutate()}
            disabled={desativar.isPending}
            className="text-[12px] font-semibold text-texto-suave hover:text-erro"
          >
            desativar
          </button>
        ) : (
          <Botao tamanho="tabela" variante="primario" onClick={() => ativar.mutate()} carregando={ativar.isPending}>
            Ativar DNSSEC
          </Botao>
        )}
      </div>

      {data.assinada && (
        <div className="mt-3 space-y-2.5">
          <p className="text-[12px] leading-relaxed text-texto-suave">
            Publique este registro <strong>DS</strong> no registro.br (na configuração do domínio,
            seção DNSSEC). Sem ele publicado no pai, a assinatura existe mas não é validada.
          </p>
          <ListaDs ds={data.ds} />
          <label className="flex items-center gap-2 text-[12.5px]">
            <input
              type="checkbox"
              checked={data.dsPublicado}
              onChange={(e) => publicado.mutate(e.target.checked)}
              className="size-4 accent-marca"
            />
            <span className={data.dsPublicado ? 'font-semibold text-sucesso' : 'text-texto-medio'}>
              Já publiquei o DS no registro.br
            </span>
          </label>
        </div>
      )}

      <SecaoRollover
        assinada={data.assinada}
        rollover={data.rollover}
        aoIniciar={() => iniciarRollover.mutate()}
        iniciando={iniciarRollover.isPending}
        aoVerificar={() => verificarRollover.mutate()}
        verificando={verificarRollover.isPending}
        aoCancelar={() => cancelarRollover.mutate()}
        cancelando={cancelarRollover.isPending}
        detectadoNovo={verificarRollover.data?.detectadoNovo}
      />
    </div>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1 block text-[10.5px] font-bold uppercase tracking-[0.5px] text-texto-suave">
      {children}
    </span>
  );
}

function dicaTipo(tipo: string): string {
  switch (tipo) {
    case 'A':
      return '200.150.10.1';
    case 'AAAA':
      return '2001:db8::1';
    case 'CNAME':
      return 'destino.exemplo.com';
    case 'MX':
      return '10 mail.cliente.com.br';
    case 'TXT':
      return 'v=spf1 include:_spf.provedor.com ~all';
    case 'NS':
      return 'ns1.exemplo.com';
    case 'SRV':
      return '10 60 5060 sip.cliente.com.br';
    default:
      return '';
  }
}
